import { Helmet } from 'react-helmet'
import { Box, Flex } from 'grid-styled'
import * as React from 'react'
import Layout from '../components/Layout'
import { graphql } from 'gatsby'
import Img from 'gatsby-image'

import CTA from '../components/CTA'
import Header from '../components/Header'
import {
  SplitContent,
  SplitFigure,
  Container,
  SplitSection,
  ReadMore,
  Text
} from '../components/styles'
import Arrow from '../components/Arrow'
import Testimonials from '../components/Testimonials'
import { testimonials } from './testimonials'
import styled from 'styled-components'
import topSvg from '../images/top.svg'

const Value = styled(Container).attrs({
  bg: '#fff',
  medium: true,
  shadow: true
})`
  margin-bottom: 1.5rem;
  padding: 3rem;

  > div > div:first-child {
    padding-right: 3rem;
  }
`

const StyledFlex = styled(Flex)`
  @media (max-width: 639px) {
    flex-direction: column;
    > div {
      margin-bottom: 2rem;
    }
  }
`

/* prettier-ignore */
interface IProps {
  data: {
    coast: {
      childImageSharp: {
        fluid: object
      }
    };
    ecology: {
      childImageSharp: {
        fluid: object
      }
    };
  }
}

export default ({ data }: IProps) => (
  <Layout header="light">
    <Helmet>
      <title>About - Who We Are - Turboclicks</title>
      <meta
        name="description"
        content="Our mission is to help organisations double their sales through modern growth strategies. And we're serious about it."
      />
    </Helmet>
    <Header>
      <h3>
        <Box mb={3}>
          <h5>About us</h5>
        </Box>
        Our mission is to help organisations double their sales through modern
        growth strategies
      </h3>

      <img src={topSvg} />
    </Header>

    <Container padY={true} medium={true}>
      <SplitSection>
        <SplitFigure>
          <Img
            fluid={data.ecology.childImageSharp.fluid}
            alt="Turboclicks business woman smiling"
          />
        </SplitFigure>
        <SplitContent>
          <h2>Modernising business growth</h2>
          <p>
            Realistically, your marketing practices are probably 50 years out of
            date. If you're lucky they're only a few years out of date. But the
            pace at which our world moves, a few years may as well be a
            lifetime.
          </p>
          <p>
            We're advocates of forward thinking business practices. This starts
            with the utilisation of technology to power marketing. But this is
            only a start, it certainly doesn't end here. We're students of
            cybernetics, systems thinking, strategy and ecology. Our continually
            developing understanding of these fields helps power our practices
            and keep us growing in a sustainable and self-reinforcing manner.
          </p>
          <p>
            But at the end of the day, we know you care about growth. How you
            get there is up to us. Our strategies are proven time and time again
            in a variety of industries and businesses. We are certain that we
            can help you get the result your seeking.
          </p>
          <ReadMore to="/contact">
            Powerful modern growth strategy
            <Arrow rotate="-90deg" />
          </ReadMore>
        </SplitContent>
      </SplitSection>
    </Container>

    <Box mb={5}>
      <Container full={true} padY={true}>
        <Container medium={true}>
          <h2>
            “The only way you can control your destiny is to be more flexible
            than your environment.”
          </h2>
        </Container>
      </Container>
    </Box>

    <Container
      style={{ borderRadius: 4 }}
      bg="#f1f8fe"
      padY={true}
      medium={true}
    >
      <SplitSection style={{ marginLeft: 0 }}>
        <SplitFigure>
          <Img
            fluid={data.coast.childImageSharp.fluid}
            alt="Turboclicks business woman smiling"
          />
        </SplitFigure>
        <SplitContent>
          <h5>The Turboclicks Team</h5>
          <p>
            Unfortunately, many agencies take the exact same approach to every
            client’s website. We take the time to understand your individual
            business to act effectively and truly generate results. We're a
            small local agency and value our personal touch.
          </p>
          <ReadMore to="/contact">
            Discover the power of digital strategy <Arrow rotate="-90deg" />
          </ReadMore>
        </SplitContent>
      </SplitSection>
      <Flex justifyContent="center" mt={5}>
        <h3>
          Our team is made up of hard-working, fun, and passionate people. We
          love technology and business growth.
        </h3>
      </Flex>
    </Container>

    <CTA
      title="Not sure what to do?"
      subtitle="We’d love to tell you our secrets."
      button="Speak to a specialist"
    />

    <Container padY={true} bg="#f8f8ff" full={true}>
      <Flex mb={5} justifyContent="center">
        <h3>Our core beliefs</h3>
      </Flex>
      <Value>
        <Flex>
          <div>
            <h4>Transparency</h4>
            <p>
              Don't be like Facebook. We believe that without transparency,
              nothing else can follow. Our strategic and human values pale in
              comparison to the importance of complete transparency in
              everything we do. The good news is, by being completely
              transparent we're motivated to work as diligently as possible to
              produce unparalleled results.
            </p>
          </div>
        </Flex>
      </Value>
      <Value>
        <StyledFlex>
          <div>
            <h4>Strategy</h4>
            <p>
              Of course. At Turboclicks we do not seek to wander aimlessly, or
              worse in the wrong direction. We value strategic and integral
              thinking and practice. Everything we do is deliberate and with
              reason.
            </p>
          </div>
          <div>
            <h4>Humanity</h4>
            <p>
              We're not some billion dollar corporation. We strongly feel
              business is at it's best when it's human and intimate. We value
              relationships and forge them to be strong and trusting. In a world
              that's becoming more and more digital, it can still be nice to
              have a human touch.
            </p>
          </div>
        </StyledFlex>
      </Value>
    </Container>

    <Testimonials testimonials={testimonials} />

    {/* <Container padY={true} medium={true}>
      <h3>Why Turboclicks?</h3>
      <Text size="large">
        Open google. Type in a search query. See that first result? It's there
        because someone has taken the time to get it there. This is great SEO at
        work.
      </Text>
    </Container> */}

    <CTA
      title="Having a growth issue?"
      subtitle="We’re here to help you."
      button="Contact Us"
    />
  </Layout>
)

export const query = graphql`
  query AboutPageQuery {
    ecology: file(relativePath: { eq: "business-growth-ecology.jpg" }) {
      childImageSharp {
        fluid(maxWidth: 1000) {
          ...GatsbyImageSharpFluid
        }
      }
    }

    coast: file(relativePath: { eq: "gold-coast.jpg" }) {
      childImageSharp {
        fluid(maxWidth: 1000) {
          ...GatsbyImageSharpFluid
        }
      }
    }
  }
`
